// src/services/cache/Cache.ts
export type CacheStore = "memory" | "localStorage";

type Entry<T> = {
  value: T;
  expiry: number; // Date.now() + ttl
};

export type CachedGetOptions<T> = {
  ttl?: number; // ms, default 5dk
  store?: CacheStore; // "memory" | "localStorage"
  staleWhileRevalidate?: boolean; // true => bayat veriyi döndür, arkadan yenile
  key?: string; // custom key; yoksa url+service kullanılır
  revalidateFn?: () => Promise<T>; // SWR için custom fetch
};

export class SmartCache {
  private mem = new Map<string, Entry<any>>();
  private inflight = new Map<string, Promise<any>>();

  constructor(private defaultTTL = 5 * 60 * 1000) {}

  // Key üretimi: service + url + serialized config (gerekirse)
  makeKey(service: string, url: string, extra?: string) {
    return extra ? `svc:${service}|${url}|${extra}` : `svc:${service}|${url}`;
  }

  private now() {
    return Date.now();
  }

  private readLS<T>(key: string): Entry<T> | null {
    try {
      const raw = localStorage.getItem(key);
      if (!raw) return null;
      return JSON.parse(raw);
    } catch {
      return null;
    }
  }
  private writeLS<T>(key: string, entry: Entry<T>) {
    try {
      localStorage.setItem(key, JSON.stringify(entry));
    } catch {}
  }
  private removeLS(key: string) {
    try {
      localStorage.removeItem(key);
    } catch {}
  }

  private getEntry<T>(key: string, store: CacheStore): Entry<T> | null {
    const entry =
      store === "memory"
        ? (this.mem.get(key) as Entry<T> | undefined) ?? null
        : this.readLS<T>(key);
    if (!entry) return null;
    if (entry.expiry <= this.now()) return null;
    // DEBUG:
    console.debug("[CACHE HIT]", key);
    return entry;
  }
  private setEntry<T>(key: string, value: T, ttl: number, store: CacheStore) {
    const entry: Entry<T> = { value, expiry: this.now() + ttl };
    if (store === "memory") this.mem.set(key, entry);
    else this.writeLS<T>(key, entry);
    // DEBUG:
    console.debug("[CACHE SET]", key);
  }

  private deleteEntry(key: string) {
    this.mem.delete(key);
    this.removeLS(key);
  }

  /** Ana API: cache’li GET */
  async cachedGet<T>(
    service: string,
    url: string,
    fetchFn: () => Promise<T>,
    opts: CachedGetOptions<T> = {}
  ): Promise<T> {
    const ttl = opts.ttl ?? this.defaultTTL;
    const store = opts.store ?? "memory";
    const key = opts.key ?? this.makeKey(service, url);

    const fresh = this.getEntry<T>(key, store);
    if (fresh) {
      console.debug("[CACHE RETURN FRESH]", key);
      return fresh.value;
    }

    const stale = store === "memory" ? this.mem.get(key) : this.readLS<T>(key);
    if (opts.staleWhileRevalidate && stale?.value) {
      console.debug("[CACHE RETURN STALE, REVALIDATE]", key);
      if (!this.inflight.has(key)) {
        const refetch = (opts.revalidateFn ?? fetchFn)()
          .then((data) => {
            this.setEntry<T>(key, data, ttl, store);
            this.inflight.delete(key);
            return data;
          })
          .catch(() => {
            this.inflight.delete(key);
            return stale.value;
          });
        this.inflight.set(key, refetch);
      }
      return stale.value as T;
    }

    if (this.inflight.has(key)) {
      console.debug("[CACHE INFLIGHT JOIN]", key);
      return this.inflight.get(key) as Promise<T>;
    }

    console.debug("[CACHE MISS -> FETCH]", key);
    const p = fetchFn()
      .then((data) => {
        this.setEntry<T>(key, data, ttl, store);
        this.inflight.delete(key);
        return data;
      })
      .catch((e) => {
        this.inflight.delete(key);
        throw e;
      });

    this.inflight.set(key, p);
    return p;
  }

  /** Tek anahtar sil */
  invalidate(key: string) {
    this.deleteEntry(key);
  }

  /** Prefix/pattern ile çoklu silme (hem memory hem LS) */
  invalidatePrefix(prefix: string) {
    // memory
    for (const k of Array.from(this.mem.keys())) {
      // veya [...this.mem.keys()]
      if (k.includes(prefix)) this.mem.delete(k);
    }

    // LS
    try {
      for (let i = localStorage.length - 1; i >= 0; i--) {
        const k = localStorage.key(i);
        if (k && k.includes(prefix)) localStorage.removeItem(k);
      }
    } catch {}
  }

  /** Tam temizle */
  clearAll() {
    this.mem.clear();
    try {
      localStorage.clear();
    } catch {}
  }
}

// Tekil instance
export const cache = new SmartCache(5 * 60 * 1000);
